import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HowItWorksPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/659671df2896ffc0ce475e0437197503.png" 
                alt="Seaside Vouchers" 
                className="h-16 w-16"
              />
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e0d98287e8c9dc8abdecc58291257ecc.png" 
                alt="SEASIDE VOUCHERS" 
                className="h-12"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Home</Link>
              <Link to="/how-it-works" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">How it works</Link>
              <Link to="/seaside-offers" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Seaside Vouchers App</Link>
              <Link to="/voucher-book" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Voucher Book</Link>
              <Link to="/leisure-home-sales" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Leisure Home Sales</Link>
              <Link to="/accommodation" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Accommodation</Link>
              <Link to="/contact" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Contact us</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-8 bg-gradient-to-br from-green-400 to-green-500 overflow-hidden">
        {/* Animated Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-300 rounded-full opacity-20 blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-cyan-300 rounded-full opacity-20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-white rounded-full opacity-10 blur-2xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center text-white mb-6">
            <h1 className="text-5xl font-bold mb-3 drop-shadow-lg">How our Seaside Vouchers App works</h1>
            <p className="text-xl max-w-3xl mx-auto drop-shadow-md">Discover how easy it is to save money on your seaside adventures with Seaside Vouchers App</p>
          </div>
        </div>
      </section>

      {/* Main Benefits */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-3">WHY USE SEASIDE VOUCHERS</h2>
          <p className="text-center text-gray-700 text-2xl font-semibold mb-8 max-w-2xl mx-auto">
            Save money on your seaside adventures with our free app
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-orange-500 rounded-full">
                <i className="ri-download-cloud-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Free Download</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-500 mt-0.5"></i>
                  <span>No upfront payment</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-500 mt-0.5"></i>
                  <span>No Subscription</span>
                </li>
              </ul>
              <Link to="/start-saving" className="inline-block mt-4 text-orange-500 font-semibold text-sm hover:text-orange-600 cursor-pointer whitespace-nowrap">
                Get The APP Here →
              </Link>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-cyan-500 rounded-full">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Access discounts on the go</h3>
              <p className="text-sm text-gray-700">
                Start saving on your next trip to the Great British Seaside
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-green-500 rounded-full">
                <i className="ri-group-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Any One can use it</h3>
              <p className="text-sm text-gray-700">
                The whole family can download and use Seaside Vouchers. Some offers may vary on their age limit depending on the attraction
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-purple-500 rounded-full">
                <i className="ri-ticket-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Vouchers can be used multiple times</h3>
              <p className="text-sm text-gray-700">
                Yes, that's right. You can use our offers as many times as you wish. Your on holiday, enjoy yourself!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Step by Step Guide */}
      <section className="py-12 bg-gradient-to-b from-cyan-50 to-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-8">Getting Started is Easy</h2>
          
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0 w-24 h-24 flex items-center justify-center bg-orange-500 text-white rounded-full text-4xl font-bold">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Download the App</h3>
                <p className="text-gray-700 mb-4">
                  Download Seaside Vouchers for free from the App Store or Google Play. No payment required, no subscription fees.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="https://apps.apple.com/pk/app/seaside-vouchers-user/id6745555629" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                    <img 
                      src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1726012800" 
                      alt="Download on App Store" 
                      className="h-12"
                    />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.jeuxdevelopers.SeasideUser&pcampaignid=web_share&pli=1" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                    <img 
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                      alt="Get it on Google Play" 
                      className="h-12"
                    />
                  </a>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0 w-24 h-24 flex items-center justify-center bg-cyan-500 text-white rounded-full text-4xl font-bold">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Create Your Account</h3>
                <p className="text-gray-700">
                  Sign up with your email address in just a few seconds. Enable location services to see offers near you and turn on notifications to never miss a deal.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0 w-24 h-24 flex items-center justify-center bg-green-500 text-white rounded-full text-4xl font-bold">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Browse Offers</h3>
                <p className="text-gray-700">
                  Explore dozens of money-saving offers for attractions, restaurants, shopping, and more. Filter by category or location to find exactly what you need.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0 w-24 h-24 flex items-center justify-center bg-purple-500 text-white rounded-full text-4xl font-bold">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Redeem Your Voucher</h3>
                <p className="text-gray-700">
                  Simply show the voucher on your phone at the business or use the discount code for online bookings. It's that easy! Use vouchers as many times as you want.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-8">App Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-orange-500 rounded-full">
                <i className="ri-notification-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Push Notifications</h3>
              <p className="text-sm text-gray-600">
                Get instant alerts when new offers are added near your location
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-cyan-500 rounded-full">
                <i className="ri-map-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Location-Based</h3>
              <p className="text-sm text-gray-600">
                See the best offers based on your current location
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-green-500 rounded-full">
                <i className="ri-search-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Easy Search</h3>
              <p className="text-sm text-gray-600">
                Find offers quickly by category, location, or business name
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-purple-500 rounded-full">
                <i className="ri-bookmark-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Save Favourites</h3>
              <p className="text-sm text-gray-600">
                Bookmark your favourite offers for quick access later
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-orange-500 rounded-full">
                <i className="ri-refresh-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Unlimited Use</h3>
              <p className="text-sm text-gray-600">
                Use each voucher multiple times throughout your holiday
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-4 bg-cyan-500 rounded-full">
                <i className="ri-user-settings-line text-3xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Easy Management</h3>
              <p className="text-sm text-gray-600">
                Update your profile and preferences anytime in settings
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-orange-500">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Start Saving?</h2>
          <p className="text-xl text-white mb-6">Download the app now and enjoy exclusive seaside offers</p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://apps.apple.com/pk/app/seaside-vouchers-user/id6745555629" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img 
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1726012800" 
                alt="Download on App Store" 
                className="h-14"
              />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.jeuxdevelopers.SeasideUser&pcampaignid=web_share&pli=1" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img 
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                alt="Get it on Google Play" 
                className="h-14"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-cyan-500 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/659671df2896ffc0ce475e0437197503.png" 
                alt="Seaside Vouchers" 
                className="h-20 w-20 transform hover:scale-110 transition-transform duration-300"
              />
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e0d98287e8c9dc8abdecc58291257ecc.png" 
                alt="SEASIDE VOUCHERS" 
                className="h-10"
              />
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Quick Link</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Home</Link></li>
                <li><Link to="/how-it-works" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">How it works</Link></li>
                <li><Link to="/seaside-offers" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Seaside offers</Link></li>
                <li><Link to="/voucher-book" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Voucher Book</Link></li>
                <li><Link to="/leisure-home-sales" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Leisure Home Sales</Link></li>
                <li><Link to="/accommodation" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Accommodation</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://seasidevouchers.co.uk/privacy-policy/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Privacy &amp; cookie policy</a></li>
                <li><a href="https://seasidevouchers.co.uk/terms-conditions/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">T&amp;C</a></li>
                <li><Link to="/contact" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Contact us</Link></li>
                <li><Link to="/business-registration" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Add Your Business</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Follow Us</h4>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/people/seasidevouchers/61566031950810/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/seaside_vouchers/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="https://www.tiktok.com/@seasidevouchers" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-tiktok-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 text-center text-sm">
            <p>Copyright © 2023 Seaside Voucher, All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
